<template>
  <div class="finance-payment-create-for-master">
    <q-stepper v-model="wizardStep" color="primary" animated flat header-nav>
      <q-step :name="1" title="Исключить проекты" icon="block">
        <q-banner rounded class="wizard-desc q-mb-md">
          <div class="row items-center">
            <div>
              Шаг 1: при необходимости исключите проекты от оплаты.
              Затем переходите к предпросмотру платежей.
            </div>
          </div>
        </q-banner>

        <div class="row q-gutter-sm">
          <q-btn
            outline
            color="deep-orange-6"
            icon="block"
            label="Открыть исключения"
            @click="goToExcludeProjects"
          />
          <q-btn
            color="primary"
            icon="navigate_next"
            label="Далее"
            @click="wizardStep = 2"
          />
        </div>
      </q-step>

      <q-step :name="2" title="Предпросмотр платежей" icon="preview">
        <q-banner rounded class="wizard-desc q-mb-md">
          <div class="row items-center">
            <div>
              Шаг 2: получите предпросмотр. Можно выбрать проект, а можно оставить пустым (будет по всем).
              Выбор проектов для создания делается чекбоксами в таблице.
            </div>
          </div>
        </q-banner>

        <div class="filters-section">
          <div class="filters-row">
            <div class="filter-item filter-item--grow">
              <label>Проект:</label>
              <div class="filter-with-clear">
                <div class="filter-input-wrapper filter-input-wrapper--grow" @click="openProjectModal">
                  <q-input
                    :model-value="selectedProjectLabel"
                    outlined
                    dense
                    readonly
                    placeholder="Выберите проект"
                    class="project-input project-input--grow"
                  >
                    <template v-slot:prepend>
                      <q-icon name="apartment" color="primary" />
                    </template>
                  </q-input>
                </div>
                <q-btn
                  v-if="filters.project_id"
                  flat
                  round
                  dense
                  icon="close"
                  size="sm"
                  color="grey-7"
                  class="clear-btn"
                  @click="clearProject"
                />
              </div>
            </div>

            <div class="filter-actions">
              <q-btn
                color="primary"
                icon="refresh"
                label="Получить"
                :loading="isFetching"
                @click="getData"
              />
              <q-btn color="grey-7" flat label="Сбросить" @click="reset" />
            </div>
          </div>
        </div>

        <q-banner
          v-if="notices && notices.length"
          class="notices-banner q-mb-sm"
          rounded
        >
          <div class="row items-center">
            <q-icon name="info" color="primary" class="q-mr-sm" />
            <div class="text-weight-medium">Сообщения (notice) от сервера:</div>
          </div>
          <div class="q-mt-sm">
            <div v-for="(n, idx) in notices" :key="idx" class="notice-line">
              {{ n }}
            </div>
          </div>
        </q-banner>

        <q-banner v-if="rowsData && rowsData.length" class="wizard-desc wizard-desc--compact q-mb-sm" rounded>
          <div class="row items-center">
            <div>
              <template v-if="isDetailMode">
                Группировка по <b>project_id</b>. Внутри проекта — строки <b>remont_costs</b>.
                Можно выбрать весь проект или отдельные строки. Если ничего не выбрать — создание пойдет по всем строкам предпросмотра.
              </template>
              <template v-else>
                Выбор проектов делается в таблице (чекбокс). Выбор идёт по <b>project_id</b>.
                Если ничего не выбрать — создание пойдет по всем строкам предпросмотра.
              </template>
            </div>
          </div>
        </q-banner>

        <div v-if="isDetailMode && rowsData && rowsData.length" class="step2-totals-bar q-mb-sm">
          <q-icon name="functions" color="primary" size="xs" class="q-mr-xs" />
          <template v-if="selectedRemontCostsIds.length > 0">
            Выбрано: <b class="q-mx-xs">{{ selectedRemontCostsIds.length }}</b> строк на сумму
            <b class="q-mx-xs text-positive">{{ formatAmount(step3SelectedAmount) }}</b>
            из {{ rowsData.length }} строк / {{ formatAmount(step3TotalAmount) }}
          </template>
          <template v-else>
            Всего строк: <b class="q-mx-xs">{{ rowsData.length }}</b> на сумму
            <b class="q-mx-xs text-positive">{{ formatAmount(step3TotalAmount) }}</b>
            — выберите строки или оставьте пустым (по всем)
          </template>
        </div>

        <div class="table-section">
          <CustomTable
            v-if="isDetailMode"
            :title="tableTitle"
            :rowsData="projectGroups"
            :columns="detailColumns"
            :loading="isFetching"
            :custom-body="true"
            :show-search="true"
          >
            <template #customBody="props">
              <q-tr :props="props">
                <q-td key="expand" :props="props">
                  <q-btn
                    flat
                    dense
                    round
                    color="primary"
                    :icon="isProjectExpanded(props.row.project_id) ? 'expand_less' : 'expand_more'"
                    @click="toggleProjectExpand(props.row.project_id)"
                  />
                </q-td>
                <q-td key="select" :props="props">
                  <q-checkbox
                    :model-value="projectSelectionState(props.row.project_id)"
                    @update:model-value="() => toggleProjectRemontCosts(props.row.project_id)"
                    dense
                    color="positive"
                    toggle-indeterminate
                  />
                </q-td>
                <q-td key="project_info" :props="props">
                  <div class="project-info-cell">
                    <div class="project-name-row">
                      <q-icon name="apartment" color="primary" size="xs" class="q-mr-xs" />
                      <span class="resident-name">{{ props.row.resident_name || "Без названия" }}</span>
                    </div>
                    <div class="project-details-row">
                      <q-badge color="blue-grey-2" text-color="blue-grey-8" class="q-mr-sm">
                        {{ props.row.project_type_name || "Тип не указан" }}
                      </q-badge>
                      <span class="project-id">ID: {{ props.row.project_id }}</span>
                      <span class="q-ml-sm text-grey-7">
                        строк: {{ props.row._row_cnt }},
                        подрядч.: {{ props.row._contractor_cnt }},
                        аванс: {{ props.row._avans_cnt }},
                        на платеж: {{ props.row._pay_cnt }}
                      </span>
                    </div>
                  </div>
                </q-td>
                <q-td key="summary" :props="props" class="text-right">
                  <div class="payment-sum-cell">
                    <template v-if="getProjectSelectedSum(props.row.project_id) !== null">
                      <div class="amount">{{ formatAmount(getProjectSelectedSum(props.row.project_id)) }}</div>
                      <div class="amount-total-sub">из {{ formatAmount(props.row._sum) }}</div>
                    </template>
                    <template v-else>
                      <div class="amount">{{ formatAmount(props.row._sum) }}</div>
                    </template>
                  </div>
                </q-td>
              </q-tr>

              <q-tr v-show="isProjectExpanded(props.row.project_id)">
                <q-td colspan="100%">
                  <q-markup-table dense flat bordered class="detail-table">
                    <thead>
                      <tr>
                        <th style="width: 44px"></th>
                        <th class="text-left" style="width: 100px">remont_costs_id</th>
                        <th class="text-left">Работы / место</th>
                        <th class="text-left">Договор</th>
                        <th class="text-left">Подрядчик</th>
                        <th class="text-left">Операция</th>
                        <th class="text-right">Сумма</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="d in getProjectItems(props.row.project_id)"
                        :key="d.remont_costs_id"
                      >
                        <td>
                          <q-checkbox
                            :model-value="isRemontCostSelected(d.remont_costs_id)"
                            @update:model-value="() => toggleRemontCost(d.remont_costs_id)"
                            dense
                            color="positive"
                          />
                        </td>
                        <td class="text-left" style="font-size:12px; color:#607d8b; font-weight:600">
                          {{ d.remont_costs_id }}
                        </td>
                        <td class="text-left">
                          <div class="report-line">
                            <q-icon name="fact_check" color="teal" size="xs" class="q-mr-xs" />
                            <span class="report-strong">{{ d.work_set_check_group_name || "—" }}</span>
                          </div>
                          <div v-if="d.block_name || d.level_num != null" class="report-sub">
                            <q-icon name="grid_view" color="grey-7" size="xs" class="q-mr-xs" />
                            <span>блок: {{ d.block_name || "—" }}</span>
                            <span v-if="d.level_num != null" class="q-ml-sm">эт. {{ d.level_num }}</span>
                          </div>
                        </td>
                        <td class="text-left">
                          <div class="report-line">
                            <q-icon name="description" color="secondary" size="xs" class="q-mr-xs" />
                            <span class="report-strong">{{ d.agreement_num || "-" }}</span>
                            <span v-if="d.agreement_date" class="q-ml-sm text-grey-7">от {{ formatDate(d.agreement_date) }}</span>
                          </div>
                        </td>
                        <td class="text-left">
                          <div class="report-line">
                            <q-icon name="business" color="grey-7" size="xs" class="q-mr-xs" />
                            <span class="report-strong">{{ d.contractor_name || "-" }}</span>
                          </div>
                        </td>
                        <td class="text-left">
                          <q-badge
                            :color="(d.what_operation || '') === 'Аванс' ? 'deep-orange-4' : 'green-5'"
                            text-color="white"
                          >
                            {{ d.what_operation || "—" }}
                          </q-badge>
                        </td>
                        <td class="text-right">
                          <span class="report-strong">{{ formatAmount(d.payment_amount ?? d.payment_sum) }}</span>
                        </td>
                      </tr>
                    </tbody>
                  </q-markup-table>
                </q-td>
              </q-tr>
            </template>
          </CustomTable>

          <CustomTable
            v-else
            :title="tableTitle"
            :rowsData="rowsData"
            :columns="columns"
            :loading="isFetching"
            :custom-body="true"
            :show-search="true"
          >
            <template #customBody="props">
              <q-tr :props="props">
                <q-td key="select" :props="props">
                  <q-checkbox
                    :model-value="isProjectSelected(props.row.project_id)"
                    @update:model-value="() => toggleProject(props.row.project_id)"
                    dense
                    color="positive"
                  />
                </q-td>
                <q-td key="project_info" :props="props">
                  <div class="project-info-cell">
                    <div class="project-name-row">
                      <q-icon name="apartment" color="primary" size="xs" class="q-mr-xs" />
                      <span class="resident-name">{{ props.row.resident_name || "Без названия" }}</span>
                    </div>
                    <div class="project-details-row">
                      <q-badge color="blue-grey-2" text-color="blue-grey-8" class="q-mr-sm">
                        {{ props.row.project_type_name || "Тип не указан" }}
                      </q-badge>
                      <span class="project-id">ID: {{ props.row.project_id }}</span>
                    </div>
                  </div>
                </q-td>
                <q-td key="agreement_info" :props="props">
                  <div class="agreement-info-cell">
                    <div class="agreement-num-row">
                      <q-icon name="description" color="secondary" size="xs" class="q-mr-xs" />
                      <span class="agreement-num">
                        № {{ props.row.agreement_num || "б/н" }}
                        <span v-if="props.row.agreement_date" class="agreement-date">
                          от {{ formatDate(props.row.agreement_date) }}
                        </span>
                      </span>
                    </div>
                    <div class="agreement-sum-row">
                      <span class="text-grey-7">Сумма: </span>
                      <span class="agreement-sum">{{ formatAmount(props.row.agreement_sum) }}</span>
                    </div>
                  </div>
                </q-td>
                <q-td key="contractor_name" :props="props">
                  <div class="contractor-cell">
                    <q-icon name="business" color="grey-6" size="xs" class="q-mr-xs" />
                    <span class="contractor-name">{{ props.row.contractor_name || "-" }}</span>
                  </div>
                </q-td>
                <q-td key="payment_sum" :props="props" class="text-right">
                  <div class="payment-sum-cell">
                    <div class="amount">{{ formatAmount(props.row.payment_sum) }}</div>
                  </div>
                </q-td>
              </q-tr>
            </template>
          </CustomTable>
        </div>

        <q-stepper-navigation>
          <q-btn flat label="Назад" icon="navigate_before" @click="wizardStep = 1" />
          <q-btn
            color="primary"
            label="Далее"
            icon-right="navigate_next"
            :disable="!rowsData || rowsData.length === 0"
            @click="wizardStep = 3"
          />
        </q-stepper-navigation>
      </q-step>

      <q-step :name="3" title="Создать платежи" icon="paid">
        <div class="create-section">
          <div class="create-title">
            <q-icon name="paid" color="positive" class="q-mr-sm" />
            <span>Создание платежей</span>
          </div>

          <q-banner class="wizard-desc q-mb-md" rounded>
            <div class="row items-center">
              <div>
                <template v-if="isDetailMode">
                  Выберите строки <b>remont_costs</b> (или целиком проект) на шаге 2, либо ничего не выбирайте — тогда создадим по всем строкам предпросмотра (отправим null).
                </template>
                <template v-else>
                  Выберите проекты в шаге 2 (чекбокс в таблице) или ничего не выбирайте — тогда платежи создадутся по всем строкам предпросмотра (отправим null).
                </template>
              </div>
            </div>
          </q-banner>

          <q-banner class="wizard-desc q-mb-md" rounded>
            <div class="row items-center">
              <div class="text-weight-medium">Создание может занять до 30 секунд.</div>
            </div>
          </q-banner>

          <div class="create-row">
            <div class="filter-item filter-item--grow">
              <label>Выбор проектов:</label>
              <div class="create-hint">
                <template v-if="isDetailMode">
                  Выбрано строк: <b>{{ selectedRemontCostsIds.length }}</b>.
                  Если 0 — создадим по всем строкам предпросмотра (отправим null).
                </template>
                <template v-else>
                  Выбрано проектов: <b>{{ selectedProjectIds.length }}</b>.
                  Если 0 — создадим по всем строкам предпросмотра (отправим null).
                </template>
              </div>
            </div>

            <div class="filter-actions">
              <q-btn
                color="positive"
                icon="add_circle"
                label="Создать платежи"
                :loading="isCreatingPayments"
                :disable="isFetching || !rowsData || rowsData.length === 0"
                @click="createPayments"
              />
              <q-btn
                flat
                color="grey-7"
                icon="clear_all"
                label="Снять выбор"
                :disable="!rowsData || rowsData.length === 0 || (isDetailMode ? selectedRemontCostsIds.length === 0 : selectedProjectIds.length === 0)"
                @click="clearSelection"
              />
            </div>
          </div>
        </div>

        <!-- Детальный режим: показываем выбранные remont_costs, сгруппированные по проекту -->
        <div v-if="isDetailMode && rowsData && rowsData.length" class="q-mb-md">
          <div v-if="selectedRemontCostsIds.length === 0" class="step3-all-hint q-mb-sm">
            <q-icon name="info" color="primary" class="q-mr-sm" />
            Ничего не выбрано — будет создано по <b>всем {{ rowsData.length }} строкам</b> предпросмотра на сумму <b>{{ formatAmount(step3TotalAmount) }}</b>.
          </div>
          <div v-else class="step3-all-hint q-mb-sm">
            <q-icon name="check_circle" color="positive" class="q-mr-sm" />
            Выбрано строк: <b>{{ selectedRemontCostsIds.length }}</b> из {{ rowsData.length }}.&nbsp;&nbsp;
            На сумму: <b>{{ formatAmount(step3SelectedAmount) }}</b> из {{ formatAmount(step3TotalAmount) }}.
          </div>

          <div v-for="g in step3DetailGroups" :key="g.project_id" class="step3-project-block q-mb-sm">
            <div class="step3-project-header">
              <q-icon name="apartment" color="primary" size="xs" class="q-mr-xs" />
              <span class="step3-project-title">{{ g.resident_name || "Без названия" }}</span>
              <q-badge color="blue-grey-2" text-color="blue-grey-8" class="q-mx-sm">{{ g.project_type_name || "—" }}</q-badge>
              <span class="text-grey-7" style="font-size:11px">ID: {{ g.project_id }}</span>
              <q-space />
              <span class="step3-sum">{{ formatAmount(g._sum) }}</span>
            </div>
            <q-markup-table dense flat bordered class="step3-detail-table">
              <thead>
                <tr>
                  <th class="text-left" style="width: 100px">remont_costs_id</th>
                  <th class="text-left">Работы / место</th>
                  <th class="text-left">Договор</th>
                  <th class="text-left">Подрядчик</th>
                  <th class="text-left">Операция</th>
                  <th class="text-right">Сумма</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="d in g.items" :key="d.remont_costs_id">
                  <td class="text-left" style="font-size:12px; color:#607d8b; font-weight:600">
                    {{ d.remont_costs_id }}
                  </td>
                  <td class="text-left">
                    <div class="report-line">
                      <q-icon name="fact_check" color="teal" size="xs" class="q-mr-xs" />
                      <span class="report-strong">{{ d.work_set_check_group_name || "—" }}</span>
                    </div>
                    <div v-if="d.block_name || d.level_num != null" class="report-sub">
                      <q-icon name="grid_view" color="grey-7" size="xs" class="q-mr-xs" />
                      <span>блок: {{ d.block_name || "—" }}</span>
                      <span v-if="d.level_num != null" class="q-ml-sm">эт. {{ d.level_num }}</span>
                    </div>
                  </td>
                  <td class="text-left">
                    <span class="report-strong">{{ d.agreement_num || "-" }}</span>
                  </td>
                  <td class="text-left">{{ d.contractor_name || "-" }}</td>
                  <td class="text-left">
                    <q-badge
                      :color="(d.what_operation || '') === 'Аванс' ? 'deep-orange-4' : 'green-5'"
                      text-color="white"
                    >
                      {{ d.what_operation || "—" }}
                    </q-badge>
                  </td>
                  <td class="text-right">
                    <span class="report-strong">{{ formatAmount(d.payment_amount) }}</span>
                  </td>
                </tr>
              </tbody>
            </q-markup-table>
          </div>
        </div>

        <!-- Старый режим (legacy): список по проектам -->
        <q-list v-else-if="!isDetailMode && rowsData && rowsData.length" bordered separator class="rounded-borders q-mb-md">
          <q-item v-for="p in previewProjects" :key="p.project_id">
            <q-item-section>
              <q-item-label>{{ p.label }}</q-item-label>
              <q-item-label caption>строк в предпросмотре: {{ p.row_cnt }}</q-item-label>
            </q-item-section>
          </q-item>
        </q-list>

        <q-banner
          v-if="createMessage || (createNotices && createNotices.length)"
          class="create-result-banner q-mb-sm"
          rounded
        >
          <div class="row items-center">
            <q-icon name="check_circle" color="positive" class="q-mr-sm" />
            <div class="text-weight-medium">
              {{ createMessage || "Результат создания платежей" }}
              <span v-if="createdCnt !== null && createdCnt !== undefined" class="q-ml-sm">
                (создано: {{ createdCnt }})
              </span>
            </div>
          </div>
          <div v-if="createNotices && createNotices.length" class="q-mt-sm">
            <div class="text-caption text-grey">Notice:</div>
            <div v-for="(n, idx) in createNotices" :key="idx" class="notice-line">
              {{ n }}
            </div>
          </div>
        </q-banner>

        <q-stepper-navigation>
          <q-btn flat label="Назад" icon="navigate_before" @click="wizardStep = 2" />
          <q-btn
            color="primary"
            label="Далее"
            icon-right="navigate_next"
            @click="wizardStep = 4"
          />
        </q-stepper-navigation>
      </q-step>

      <q-step :name="4" title="Отправить платежи" icon="send">
        <q-banner class="wizard-desc q-mb-md" rounded>
          <div class="row items-center">
            <div>
              Отправка платежей в BI. Операция может занять до 30 секунд.
              Вызывается endpoint <b>/project/cron/payment/send/</b> без выбора проекта.
            </div>
          </div>
        </q-banner>

        <div class="row q-gutter-sm q-mb-md">
          <q-btn
            color="primary"
            icon="send"
            label="Отправить платежи"
            :loading="isSendingPayments"
            @click="sendPayments"
          />
        </div>

        <q-banner v-if="sendMessage" class="q-mb-sm" rounded>
          <div class="row items-center">
            <q-icon name="check_circle" color="positive" class="q-mr-sm" />
            <div class="text-weight-medium">{{ sendMessage }}</div>
          </div>
        </q-banner>

        <q-stepper-navigation>
          <q-btn flat label="Назад" icon="navigate_before" @click="wizardStep = 3" />
          <q-btn
            color="primary"
            label="Далее"
            icon-right="navigate_next"
            @click="wizardStep = 5"
          />
        </q-stepper-navigation>
      </q-step>

      <q-step :name="5" title="Отчет" icon="fact_check">
        <q-banner rounded class="wizard-desc q-mb-md">
          <div class="row items-center">
            <div>
              Шаг 5: отчет по созданным платежам. Нажмите на стрелку у строки — откроется расшифровка.
            </div>
          </div>
        </q-banner>

        <div class="filters-section">
          <div class="filters-row report-filters-row">
            <div class="report-date-filters-slot">
              <template v-if="!reportWithError">
                <div class="filter-item">
                  <label>Дата от:</label>
                  <q-input
                    v-model="reportDateFrom"
                    type="date"
                    outlined
                    dense
                    class="date-input date-input--sm"
                    @update:model-value="onReportDateManualChange"
                  />
                </div>
                <div class="filter-item">
                  <label>Дата по:</label>
                  <q-input
                    v-model="reportDateTo"
                    type="date"
                    outlined
                    dense
                    class="date-input date-input--sm"
                    @update:model-value="onReportDateManualChange"
                  />
                </div>
                <div class="filter-item filter-item--grow">
                  <label>Быстрые даты:</label>
                  <div class="row q-gutter-sm">
                    <q-btn
                      :outline="reportQuickRangeDays !== 0"
                      :unelevated="reportQuickRangeDays === 0"
                      :color="reportQuickRangeDays === 0 ? 'primary' : 'grey-8'"
                      label="Сегодня"
                      @click="setReportQuickRangeAndFetch(0)"
                    />
                    <q-btn
                      :outline="reportQuickRangeDays !== 7"
                      :unelevated="reportQuickRangeDays === 7"
                      :color="reportQuickRangeDays === 7 ? 'primary' : 'grey-8'"
                      label="7 дней"
                      @click="setReportQuickRangeAndFetch(7)"
                    />
                    <q-btn
                      :outline="reportQuickRangeDays !== 14"
                      :unelevated="reportQuickRangeDays === 14"
                      :color="reportQuickRangeDays === 14 ? 'primary' : 'grey-8'"
                      label="14 дней"
                      @click="setReportQuickRangeAndFetch(14)"
                    />
                    <q-btn
                      :outline="reportQuickRangeDays !== 30"
                      :unelevated="reportQuickRangeDays === 30"
                      :color="reportQuickRangeDays === 30 ? 'primary' : 'grey-8'"
                      label="30 дней"
                      @click="setReportQuickRangeAndFetch(30)"
                    />
                  </div>
                </div>
              </template>
            </div>
            <div class="filter-item">
              <label>Ошибки:</label>
              <q-btn-toggle
                v-model="reportErrorFilter"
                toggle-color="primary"
                :options="reportErrorOptions"
                unelevated
                @update:model-value="onReportErrorFilterChange"
              />
            </div>
            <div class="filter-actions">
              <q-btn
                color="primary"
                icon="refresh"
                label="Обновить отчет"
                :loading="isReportLoading"
                @click="getReport"
              />
            </div>
          </div>
        </div>

        <div class="table-section">
          <CustomTable
            title="Отчет по платежам"
            :rowsData="reportRowsFiltered"
            :columns="reportColumns"
            :loading="isReportLoading"
            :custom-body="true"
            :show-search="true"
          >
            <template #customBody="props">
              <q-tr :props="props">
                <q-td key="expand" :props="props">
                  <q-btn
                    flat
                    dense
                    round
                    color="primary"
                    :icon="isReportExpanded(props.row.finance_payment_id) ? 'expand_less' : 'expand_more'"
                    @click="toggleReportExpand(props.row.finance_payment_id)"
                  />
                </q-td>
                <q-td key="row_date" :props="props">
                  <div class="report-line">
                    <q-icon name="event" color="primary" size="xs" class="q-mr-xs" />
                      <span class="report-strong">{{ formatDate(props.row.row_date) || "-" }}</span>
                  </div>
                    <div class="report-sub">ID: {{ props.row.finance_payment_id }}</div>
                </q-td>
                <q-td key="contragent" :props="props">{{ props.row.contragent || "-" }}</q-td>
                <q-td key="payment_amount" :props="props" class="text-right">
                  <span class="report-strong">{{ formatAmount(props.row.payment_amount) }}</span>
                </q-td>
                <q-td key="error_bi" :props="props">
                  <div class="report-line">
                    <q-icon
                      :name="props.row.error_bi ? 'error' : 'check_circle'"
                      :color="props.row.error_bi ? 'negative' : 'positive'"
                      size="xs"
                      class="q-mr-xs"
                    />
                    <span :style="{ color: props.row.error_bi ? '#c62828' : '#2e7d32', fontWeight: 700 }">
                      {{ props.row.error_bi ? props.row.error_bi : "OK" }}
                    </span>
                  </div>
                </q-td>
              </q-tr>

              <q-tr :props="props" v-show="isReportExpanded(props.row.finance_payment_id)" class="report-detail-row">
                <q-td colspan="100%">
                  <div class="report-detail-wrap">
                    <div class="row items-center q-mb-sm">
                      <q-icon name="info" color="grey-7" class="q-mr-sm" />
                      <div class="text-weight-medium">Расшифровка</div>
                      <q-space />
                      <q-btn
                        v-if="!reportDetailLoadingMap[props.row.finance_payment_id]"
                        flat
                        dense
                        icon="refresh"
                        color="grey-7"
                        label="Обновить"
                        @click="loadReportDetail(props.row.finance_payment_id, true)"
                      />
                    </div>

                    <q-inner-loading :showing="!!reportDetailLoadingMap[props.row.finance_payment_id]">
                      <q-spinner color="primary" size="2em" />
                    </q-inner-loading>

                    <div v-if="(reportDetailMap[props.row.finance_payment_id] || []).length === 0" class="text-grey-7">
                      Нет расшифровки (пусто).
                    </div>

                    <q-markup-table v-else dense flat class="report-detail-table" style="width: 100%">
                      <thead>
                        <tr>
                          <th class="text-left">Проект / работы</th>
                          <th class="text-left">Договор</th>
                          <th class="text-left">Подрядчик / ИИК</th>
                          <th class="text-right">Сумма</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(d, idx) in reportDetailMap[props.row.finance_payment_id]"
                          :key="idx"
                        >
                          <td class="text-left">
                            <div class="report-line">
                              <q-icon name="apartment" color="primary" size="xs" class="q-mr-xs" />
                              <span class="report-strong">#{{ d.project_id || "-" }}</span>
                              <span class="q-ml-sm text-grey-7">{{ d.project_type_name || "-" }}</span>
                            </div>
                            <div class="report-sub">{{ d.resident_name || "-" }}</div>
                            <div class="report-sub">
                              <q-icon name="grid_view" color="grey-7" size="xs" class="q-mr-xs" />
                              <span>{{ d.block_name || "-" }}</span>
                              <span class="q-ml-sm">этаж: {{ d.level_num ?? "-" }}</span>
                            </div>
                            <div class="report-line q-mt-xs">
                              <q-icon name="fact_check" color="teal" size="xs" class="q-mr-xs" />
                              <span class="report-strong">{{ d.work_set_check_group_name || "-" }}</span>
                            </div>
                          </td>

                          <td class="text-left">
                            <div class="report-line">
                              <q-icon name="description" color="secondary" size="xs" class="q-mr-xs" />
                              <span class="report-strong">{{ d.agreement_num || "-" }}</span>
                            </div>
                            <div class="report-sub">
                              <span v-if="d.document_date">от {{ formatDate(d.document_date) }}</span>
                              <span v-else>дата: -</span>
                              <span class="q-ml-sm">КБК: {{ d.kod_bk || "-" }}</span>
                            </div>
                          </td>

                          <td class="text-left">
                            <div class="report-line">
                              <q-icon name="business" color="grey-7" size="xs" class="q-mr-xs" />
                              <span class="report-strong">{{ d.legal_name || "-" }}</span>
                            </div>
                            <div class="report-sub">
                              <q-icon name="account_balance" color="grey-7" size="xs" class="q-mr-xs" />
                              <span>{{ d.iik || "-" }}</span>
                            </div>
                            <div class="report-sub">iik_guid: {{ d.iik_guid || "-" }}</div>
                          </td>

                          <td class="text-right">
                            <div class="report-line report-cell--right">
                              <q-icon name="paid" color="positive" size="xs" class="q-mr-xs" />
                              <span class="report-strong">{{ formatAmount(d.payment_amount) }}</span>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                      <tfoot>
                        <tr>
                          <td class="text-left" colspan="3">
                            <div class="report-line">
                              <q-icon name="functions" color="grey-7" size="xs" class="q-mr-xs" />
                              <span class="report-strong">Итого</span>
                              <span class="q-ml-sm text-grey-7">
                                строк: {{ detailCount(props.row.finance_payment_id) }}
                              </span>
                            </div>
                          </td>
                          <td class="text-right">
                            <div class="report-line report-cell--right">
                              <q-icon name="paid" color="positive" size="xs" class="q-mr-xs" />
                              <span class="report-strong">
                                {{ formatAmount(detailTotal(props.row.finance_payment_id)) }}
                              </span>
                            </div>
                          </td>
                        </tr>
                      </tfoot>
                    </q-markup-table>
                  </div>
                </q-td>
              </q-tr>
            </template>
          </CustomTable>
        </div>

        <q-stepper-navigation>
          <q-btn flat label="Назад" icon="navigate_before" @click="wizardStep = 4" />
        </q-stepper-navigation>
      </q-step>
    </q-stepper>

  </div>
</template>

<script>
import CustomTable from "@/components/common/CustomTable";
import { useAppStore } from "@/services/store/app";
import { MODAL_NAMES, PAGES } from "@/constants";
import {
  columns,
  readFinancePaymentCreateForMaster,
  createFinancePaymentCreateForMaster,
  createFinancePaymentCreateForMasterByRemontCosts,
  sendFinancePaymentsToBi,
  readFinancePaymentBiReport,
  readFinancePaymentBiReportDetail,
} from "@/views/FinancePaymentCreateForMaster/services";

export default {
  name: "FinancePaymentCreateForMaster",
  components: { CustomTable },
  data() {
    return {
      columns,
      detailColumns: [
        { name: "expand", field: "project_id", label: "", align: "left", sortable: false, style: "width: 44px" },
        { name: "select", field: "project_id", label: "", align: "left", sortable: false, style: "width: 44px" },
        { name: "project_info", field: "resident_name", label: "Проект", align: "left", sortable: true, style: "min-width: 280px" },
        { name: "summary", field: "_sum", label: "Итого", align: "right", sortable: true, style: "width: 220px" },
      ],
      rowsData: [],
      notices: [],
      wizardStep: 1,
      isCreatingPayments: false,
      createNotices: [],
      createMessage: "",
      createdCnt: null,
      selectedProjectIds: [], // legacy (старый режим предпросмотра: выбор по project_id)
      selectedRemontCostsIds: [],
      expandedProjectIds: [],
      isSendingPayments: false,
      sendMessage: "",
      sendDone: false,
      reportDateFrom: "",
      reportDateTo: "",
      isReportLoading: false,
      reportRows: [],
      reportErrorFilter: "ok",
      reportErrorOptions: [
        { label: "Без ошибки", value: "ok" },
        { label: "С ошибкой", value: "error" },
      ],
      reportQuickRangeDays: 0,
      reportExpandedIds: [],
      reportDetailMap: {},
      reportDetailLoadingMap: {},
      reportColumns: [
        { name: "expand", field: "finance_payment_id", label: "", align: "left", sortable: false, style: "width: 44px" },
        { name: "row_date", field: "row_date", label: "Дата / ID", align: "left", sortable: true, style: "width: 150px" },
        { name: "contragent", field: "contragent", label: "Контрагент", align: "left", sortable: true, style: "min-width: 260px" },
        { name: "payment_amount", field: "payment_amount", label: "Сумма", align: "right", sortable: true, style: "width: 200px" },
        { name: "error_bi", field: "error_bi", label: "BI", align: "left", sortable: true, style: "min-width: 220px" },
      ],
      isFetching: false,
      selectedProject: null,
      filters: {
        project_id: null,
      },
      currentDateTime: "",
      timer: null,
    };
  },
  computed: {
    reportRowsFiltered() {
      return this.reportRows || [];
    },
    reportWithError() {
      return this.reportErrorFilter === "error";
    },
    tableTitle() {
      return `Оплаты подрядчикам (предпросмотр) на ${this.currentDateTime}`;
    },
    isDetailMode() {
      const r0 = (this.rowsData || [])[0];
      return !!(r0 && (r0.remont_costs_id !== null && r0.remont_costs_id !== undefined));
    },
    projectGroups() {
      if (!this.isDetailMode) return [];
      const rows = this.rowsData || [];
      const map = new Map();

      for (const r of rows) {
        const pid = r?.project_id;
        if (pid === null || pid === undefined) continue;
        if (!map.has(pid)) {
          map.set(pid, {
            project_id: pid,
            resident_name: r?.resident_name || "",
            project_type_name: r?.project_type_name || "",
            items: [],
          });
        }
        map.get(pid).items.push(r);
      }

      const list = Array.from(map.values()).map((g) => {
        const sum = (g.items || []).reduce((acc, x) => acc + (Number(x?.payment_amount ?? x?.payment_sum) || 0), 0);
        const avansCnt = (g.items || []).filter((x) => (x?.what_operation || "") === "Аванс").length;
        const payCnt = (g.items || []).filter((x) => (x?.what_operation || "") !== "Аванс").length;
        const contractors = new Set((g.items || []).map((x) => x?.contractor_id).filter((x) => x != null));
        return {
          ...g,
          _row_cnt: g.items.length,
          _sum: sum,
          _avans_cnt: avansCnt,
          _pay_cnt: payCnt,
          _contractor_cnt: contractors.size,
        };
      });

      return list.sort((a, b) => (a.project_id || 0) - (b.project_id || 0));
    },
    step3DetailRows() {
      if (!this.isDetailMode) return [];
      const rows = this.rowsData || [];
      if (!this.selectedRemontCostsIds || this.selectedRemontCostsIds.length === 0) {
        return rows;
      }
      const selected = new Set(this.selectedRemontCostsIds);
      return rows.filter((r) => selected.has(r?.remont_costs_id));
    },
    step3SelectedAmount() {
      if (!this.isDetailMode) return 0;
      const selected = new Set(this.selectedRemontCostsIds || []);
      return (this.rowsData || [])
        .filter((r) => selected.has(r?.remont_costs_id))
        .reduce((acc, r) => acc + (Number(r?.payment_amount) || 0), 0);
    },
    step3TotalAmount() {
      if (!this.isDetailMode) return 0;
      return (this.rowsData || []).reduce((acc, r) => acc + (Number(r?.payment_amount) || 0), 0);
    },
    step3DetailGroups() {
      const rows = this.step3DetailRows;
      const map = new Map();
      for (const r of rows) {
        const pid = r?.project_id;
        if (pid == null) continue;
        if (!map.has(pid)) {
          map.set(pid, {
            project_id: pid,
            resident_name: r?.resident_name || "",
            project_type_name: r?.project_type_name || "",
            items: [],
          });
        }
        map.get(pid).items.push(r);
      }
      return Array.from(map.values()).map((g) => ({
        ...g,
        _sum: (g.items || []).reduce((acc, x) => acc + (Number(x?.payment_amount) || 0), 0),
      })).sort((a, b) => (a.project_id || 0) - (b.project_id || 0));
    },
    previewProjects() {
      const rows = this.rowsData || [];
      if (this.isDetailMode) {
        const selected = new Set(this.selectedRemontCostsIds || []);
        const useSelected = selected.size > 0;
        const map = new Map();

        for (const r of rows) {
          const pid = r?.project_id;
          const rcid = r?.remont_costs_id;
          if (pid === null || pid === undefined) continue;
          if (useSelected && !selected.has(rcid)) continue;

          if (!map.has(pid)) {
            const resident = r?.resident_name || "";
            const type = r?.project_type_name || "";
            const label = [`#${pid}`, resident, type].filter(Boolean).join(" — ");
            map.set(pid, { project_id: pid, label: label || `#${pid}`, row_cnt: 0 });
          }
          map.get(pid).row_cnt += 1;
        }
        return Array.from(map.values()).sort((a, b) => (a.project_id || 0) - (b.project_id || 0));
      }

      const selected = new Set(this.selectedProjectIds || []);
      const useSelected = selected.size > 0;

      const map = new Map();
      for (const r of rows) {
        const id = r?.project_id;
        if (id === null || id === undefined) continue;
        if (useSelected && !selected.has(id)) continue;

        if (!map.has(id)) {
          const resident = r?.resident_name || "";
          const type = r?.project_type_name || "";
          const label = [`#${id}`, resident, type].filter(Boolean).join(" — ");
          map.set(id, { project_id: id, label: label || `#${id}`, row_cnt: 0 });
        }
        map.get(id).row_cnt += 1;
      }
      return Array.from(map.values()).sort((a, b) => (a.project_id || 0) - (b.project_id || 0));
    },
    selectedProjectLabel() {
      if (!this.selectedProject) return "";
      const id = this.selectedProject.project_id ? `#${this.selectedProject.project_id}` : "";
      const resident = this.selectedProject.resident_name || "";
      const type = this.selectedProject.project_type_name || "";
      return [id, resident, type].filter(Boolean).join(" — ");
    },
  },
  mounted() {
    this.updateDateTime();
    this.timer = setInterval(this.updateDateTime, 60000);
    const today = this.todayIsoDate();
    this.reportDateFrom = today;
    this.reportDateTo = today;
  },
  beforeUnmount() {
    if (this.timer) clearInterval(this.timer);
  },
  methods: {
    todayIsoDate() {
      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    },
    setReportQuickRange(daysBack) {
      const today = new Date();
      const from = new Date(today);
      if (daysBack && daysBack > 0) {
        from.setDate(from.getDate() - daysBack);
      }
      const pad = (n) => String(n).padStart(2, "0");
      const toIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      this.reportDateFrom = toIso(from);
      this.reportDateTo = toIso(today);
    },
    onReportDateManualChange() {
      this.reportQuickRangeDays = null;
    },
    onReportErrorFilterChange() {
      this.getReport();
    },
    async setReportQuickRangeAndFetch(daysBack) {
      this.reportQuickRangeDays = daysBack;
      this.setReportQuickRange(daysBack);
      await this.getReport();
    },
    updateDateTime() {
      const now = new Date();
      this.currentDateTime = now.toLocaleString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    formatAmount(value) {
      if (value === null || value === undefined) return "-";
      return (
        new Intl.NumberFormat("ru-RU", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(value) + " ₸"
      );
    },
    detailCount(financePaymentId) {
      const rows = (this.reportDetailMap && this.reportDetailMap[financePaymentId]) || [];
      return rows.length || 0;
    },
    detailTotal(financePaymentId) {
      const rows = (this.reportDetailMap && this.reportDetailMap[financePaymentId]) || [];
      return rows.reduce((acc, r) => acc + (Number(r?.payment_amount) || 0), 0);
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return d.toLocaleDateString("ru-RU");
    },
    openProjectModal() {
      this.appStore.showSecondaryModal(MODAL_NAMES.SelectSbsProject, {
        title: "Выбор проекта",
        persistent: true,
        onSelect: (project) => {
          if (!project) return;
          this.selectedProject = project;
          this.filters.project_id = project.project_id;
        },
      });
    },
    clearProject() {
      this.filters.project_id = null;
      this.selectedProject = null;
      this.rowsData = [];
      this.selectedProjectIds = [];
      this.selectedRemontCostsIds = [];
      this.expandedProjectIds = [];
      this.createNotices = [];
      this.createMessage = "";
      this.createdCnt = null;
      this.sendMessage = "";
      this.sendDone = false;
      this.reportRows = [];
    },
    async getData() {
      this.isFetching = true;
      try {
        const res = await readFinancePaymentCreateForMaster(this.filters.project_id);
        this.rowsData = res?.rows || [];
        this.notices = res?.notices || [];
        this.selectedProjectIds = [];
        this.selectedRemontCostsIds = [];
        this.expandedProjectIds = [];
        this.createNotices = [];
        this.createMessage = "";
        this.createdCnt = null;
        this.sendMessage = "";
        this.sendDone = false;
        this.reportRows = [];

        if (this.notices.length) {
          this.$q.notify({
            type: "info",
            message: `Notice: ${this.notices.length} шт.`,
          });
        }
      } catch (e) {
        this.rowsData = [];
        this.notices = [];
        this.selectedProjectIds = [];
        this.selectedRemontCostsIds = [];
        this.expandedProjectIds = [];
        this.createNotices = [];
        this.createMessage = "";
        this.createdCnt = null;
        this.sendMessage = "";
        this.sendDone = false;
        this.reportRows = [];
        this.$q.notify({
          type: "negative",
          message: e?.response?.data?.error || "Ошибка получения данных",
        });
      } finally {
        this.isFetching = false;
      }
    },
    reset() {
      this.clearProject();
    },
    goToExcludeProjects() {
      this.$router.push(PAGES.financePaymentExcludeProjects);
    },
    clearSelection() {
      this.selectedProjectIds = [];
      this.selectedRemontCostsIds = [];
    },
    isProjectSelected(projectId) {
      if (projectId === null || projectId === undefined) return false;
      return (this.selectedProjectIds || []).includes(projectId);
    },
    toggleProject(projectId) {
      if (projectId === null || projectId === undefined) return;
      const arr = Array.isArray(this.selectedProjectIds) ? [...this.selectedProjectIds] : [];
      const idx = arr.indexOf(projectId);
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(projectId);
      this.selectedProjectIds = arr;
    },
    formatAmountPlain(value) {
      if (value === null || value === undefined) return "-";
      return new Intl.NumberFormat("ru-RU", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value);
    },
    isProjectExpanded(projectId) {
      return (this.expandedProjectIds || []).includes(projectId);
    },
    toggleProjectExpand(projectId) {
      if (projectId == null) return;
      const arr = Array.isArray(this.expandedProjectIds) ? [...this.expandedProjectIds] : [];
      const idx = arr.indexOf(projectId);
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(projectId);
      this.expandedProjectIds = arr;
    },
    getProjectItems(projectId) {
      const g = (this.projectGroups || []).find((x) => x.project_id === projectId);
      return g?.items || [];
    },
    getProjectSelectedSum(projectId) {
      const items = this.getProjectItems(projectId);
      const selected = new Set(this.selectedRemontCostsIds || []);
      if (selected.size === 0) return null;
      return items
        .filter((x) => selected.has(x?.remont_costs_id))
        .reduce((acc, x) => acc + (Number(x?.payment_amount) || 0), 0);
    },
    isRemontCostSelected(remontCostsId) {
      if (remontCostsId == null) return false;
      return (this.selectedRemontCostsIds || []).includes(remontCostsId);
    },
    toggleRemontCost(remontCostsId) {
      if (remontCostsId == null) return;
      const arr = Array.isArray(this.selectedRemontCostsIds) ? [...this.selectedRemontCostsIds] : [];
      const idx = arr.indexOf(remontCostsId);
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(remontCostsId);
      this.selectedRemontCostsIds = arr;
    },
    projectSelectionState(projectId) {
      const items = this.getProjectItems(projectId);
      const ids = items.map((x) => x?.remont_costs_id).filter((x) => x != null);
      if (!ids.length) return false;
      const selected = new Set(this.selectedRemontCostsIds || []);
      const selectedCnt = ids.filter((id) => selected.has(id)).length;
      if (selectedCnt === 0) return false;
      if (selectedCnt === ids.length) return true;
      return null; // indeterminate
    },
    toggleProjectRemontCosts(projectId) {
      const items = this.getProjectItems(projectId);
      const ids = items.map((x) => x?.remont_costs_id).filter((x) => x != null);
      if (!ids.length) return;
      const selected = new Set(this.selectedRemontCostsIds || []);
      const state = this.projectSelectionState(projectId);
      // true (все выбраны) или null (частично) → снять выбор; false (ничего) → выбрать всё
      if (state === true || state === null) {
        ids.forEach((id) => selected.delete(id));
      } else {
        ids.forEach((id) => selected.add(id));
      }
      this.selectedRemontCostsIds = Array.from(selected.values());
    },
    async createPayments() {
      if (!this.rowsData || this.rowsData.length === 0) {
        this.$q.notify({ type: "warning", message: "Сначала нажмите «Получить» (нужен предпросмотр)." });
        return;
      }

      this.isCreatingPayments = true;
      this.createNotices = [];
      this.createMessage = "";
      this.createdCnt = null;

      try {
        let res = null;
        if (this.isDetailMode) {
          const rcListToSend =
            this.selectedRemontCostsIds && this.selectedRemontCostsIds.length
              ? this.selectedRemontCostsIds
              : null;
          res = await createFinancePaymentCreateForMasterByRemontCosts(rcListToSend);
        } else {
          const projectIdListToSend =
            this.selectedProjectIds && this.selectedProjectIds.length
              ? this.selectedProjectIds
              : null;
          res = await createFinancePaymentCreateForMaster(projectIdListToSend);
        }
        this.createNotices = res?.notices || [];
        this.createMessage = res?.message || "Платежи создались";
        this.createdCnt = res?.created_cnt ?? null;

        const msg = this.createdCnt !== null && this.createdCnt !== undefined
          ? `${this.createMessage}. Создано: ${this.createdCnt}`
          : this.createMessage;

        this.$q.notify({ type: "positive", message: msg });
      } catch (e) {
        this.$q.notify({
          type: "negative",
          message: e?.response?.data?.error || "Ошибка создания платежей",
        });
      } finally {
        this.isCreatingPayments = false;
      }
    },
    async sendPayments() {
      this.isSendingPayments = true;
      this.sendMessage = "";
      this.sendDone = false;
      try {
        const res = await sendFinancePaymentsToBi();
        if (res?.error) throw new Error(res.error);
        this.sendMessage = "Платежи отправлены";
        this.sendDone = true;
        this.$q.notify({ type: "positive", message: this.sendMessage });
      } catch (e) {
        this.sendMessage = "";
        this.sendDone = false;
        this.$q.notify({
          type: "negative",
          message: e?.response?.data?.error || e?.message || "Ошибка отправки платежей",
        });
      } finally {
        this.isSendingPayments = false;
      }
    },
    async getReport() {
      this.isReportLoading = true;
      try {
        const withError = this.reportErrorFilter === "error";
        const rows = await readFinancePaymentBiReport(
          this.reportDateFrom,
          this.reportDateTo,
          withError
        );
        this.reportRows = rows || [];
      } catch (e) {
        this.reportRows = [];
        this.$q.notify({
          type: "negative",
          message: e?.response?.data?.error || "Ошибка получения отчета",
        });
      } finally {
        this.isReportLoading = false;
      }
    },
    isReportExpanded(financePaymentId) {
      return (this.reportExpandedIds || []).includes(financePaymentId);
    },
    async toggleReportExpand(financePaymentId) {
      if (!financePaymentId) return;
      const arr = Array.isArray(this.reportExpandedIds) ? [...this.reportExpandedIds] : [];
      const idx = arr.indexOf(financePaymentId);
      if (idx >= 0) {
        arr.splice(idx, 1);
        this.reportExpandedIds = arr;
        return;
      }
      arr.push(financePaymentId);
      this.reportExpandedIds = arr;
      await this.loadReportDetail(financePaymentId);
    },
    async loadReportDetail(financePaymentId, force = false) {
      if (!financePaymentId) return;
      if (!force && this.reportDetailMap && this.reportDetailMap[financePaymentId]) return;
      if (!this.reportDetailLoadingMap) this.reportDetailLoadingMap = {};
      this.reportDetailLoadingMap = { ...this.reportDetailLoadingMap, [financePaymentId]: true };
      try {
        const rows = await readFinancePaymentBiReportDetail(financePaymentId);
        this.reportDetailMap = { ...(this.reportDetailMap || {}), [financePaymentId]: rows || [] };
      } catch (e) {
        this.reportDetailMap = { ...(this.reportDetailMap || {}), [financePaymentId]: [] };
        this.$q.notify({
          type: "negative",
          message: e?.response?.data?.error || "Ошибка получения расшифровки",
        });
      } finally {
        this.reportDetailLoadingMap = { ...this.reportDetailLoadingMap, [financePaymentId]: false };
      }
    },
  },
  setup() {
    const appStore = useAppStore();
    return { appStore };
  },
};
</script>

<style scoped>
.finance-payment-create-for-master {
  padding: 20px;
  background: #f8f9fa;
  min-height: 100vh;
}

.filters-section {
  background: white;
  padding: 12px 16px;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  margin-bottom: 12px;
}

.filters-row {
  display: flex;
  gap: 20px;
  align-items: flex-end;
  flex-wrap: wrap;
}

.report-filters-row {
  display: flex;
  gap: 20px;
  align-items: flex-end;
  flex-wrap: wrap;
}

.report-date-filters-slot {
  display: flex;
  gap: 20px;
  align-items: flex-end;
  flex-wrap: wrap;
  min-width: 640px;
  flex: 1;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 240px;
  flex-shrink: 0;
}

.filter-item--grow {
  flex: 1;
  min-width: 320px;
}

.filter-item label {
  font-size: 10px;
  font-weight: 600;
  color: #9e9e9e;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 0;
}

.filter-with-clear {
  display: flex;
  align-items: center;
  gap: 2px;
}

.filter-input-wrapper {
  cursor: pointer;
}

.filter-input-wrapper--grow {
  width: 100%;
}

.project-input {
  width: 160px;
  cursor: pointer;
}

.project-input--grow {
  width: 100%;
  max-width: 100%;
}

.project-input :deep(.q-field__control) {
  cursor: pointer;
}

.date-input :deep(.q-field__control) {
  min-height: 40px;
}

.date-input :deep(input) {
  font-size: 14px;
}

.date-input--sm :deep(.q-field__control) {
  min-height: 32px;
}

.date-input--sm :deep(input) {
  font-size: 12px;
}

.report-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.report-cell--right {
  align-items: flex-end;
}

.report-line {
  display: flex;
  align-items: center;
  line-height: 1.2;
}

.report-strong {
  font-weight: 700;
  color: #263238;
}

.report-sub {
  font-size: 11px;
  color: #607d8b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 360px;
}

.report-detail-wrap {
  background: #ffffff;
  border: 1px solid rgba(25, 118, 210, 0.18);
  border-left: 6px solid rgba(25, 118, 210, 0.85);
  border-radius: 14px;
  padding: 12px 14px;
  margin: 10px 0 14px;
  box-shadow: 0 10px 24px rgba(16, 24, 40, 0.10);
}

.report-detail-row :deep(td) {
  background: linear-gradient(180deg, #f7fbff 0%, #ffffff 100%);
}

.report-detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 10px;
}

.report-detail-card {
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 10px;
  padding: 10px 12px;
}

.report-detail-table {
  width: 100%;
  display: block;
}

.report-detail-table :deep(.q-table) {
  width: 100%;
  table-layout: fixed;
}

.report-detail-table thead th {
  font-size: 12px;
  color: #546e7a;
  font-weight: 600;
  background: #f5f7fb;
}

.report-detail-table tbody td {
  vertical-align: top;
}

.report-detail-table :deep(tfoot td) {
  background: #f5f7fb;
}

.report-detail-table :deep(tfoot td:last-child) {
  text-align: right;
}

.wizard-desc {
  font-size: 19px;
  line-height: 1.5;
  color: #1f2937;
  font-weight: 600;
  background: linear-gradient(180deg, #ffffff 0%, #f7fbff 100%);
  border: 1px solid rgba(25, 118, 210, 0.20);
  box-shadow: 0 8px 20px rgba(16, 24, 40, 0.06);
}

.wizard-desc--compact {
  font-size: 17px;
}

.wizard-desc :deep(.q-banner__content) {
  padding: 16px 18px;
  font-size: 19px !important;
  font-weight: 600;
  line-height: 1.5;
}

.wizard-desc--compact :deep(.q-banner__content) {
  font-size: 17px !important;
}


.clear-btn {
  margin-left: -6px;
}

.filter-actions {
  display: flex;
  gap: 6px;
  margin-left: auto;
  align-items: flex-end;
}

.filter-actions .q-btn {
  font-size: 12px;
  padding: 4px 12px;
  min-height: 32px;
}

.create-section {
  background: white;
  padding: 14px 16px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 12px;
}

.create-title {
  display: flex;
  align-items: center;
  font-weight: 700;
  color: #455a64;
  margin-bottom: 10px;
}

.create-row {
  display: flex;
  gap: 16px;
  align-items: flex-end;
  flex-wrap: wrap;
}

.create-hint {
  margin-top: 6px;
  font-size: 11px;
  color: #757575;
}

.table-section {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.notices-banner {
  background: #eef5ff;
  border: 1px solid rgba(25, 118, 210, 0.25);
}

.create-result-banner {
  background: #eefaf0;
  border: 1px solid rgba(46, 125, 50, 0.25);
}

.notice-line {
  font-size: 12px;
  color: #455a64;
  white-space: pre-wrap;
}

.project-info-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.project-name-row {
  display: flex;
  align-items: center;
}
.resident-name {
  font-weight: 600;
  font-size: 13px;
  color: #1976d2;
}
.project-details-row {
  display: flex;
  align-items: center;
}
.project-id {
  font-size: 11px;
  color: #757575;
}

.agreement-info-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.agreement-num-row {
  display: flex;
  align-items: center;
  font-weight: 500;
}
.agreement-date {
  font-weight: 400;
  color: #616161;
  margin-left: 4px;
}
.agreement-sum-row {
  font-size: 12px;
}
.agreement-sum {
  font-weight: 600;
  color: #455a64;
}

.contractor-cell {
  display: flex;
  align-items: center;
}
.contractor-name {
  font-weight: 500;
}

.payment-sum-cell {
  display: flex;
  justify-content: flex-end;
}
.amount {
  font-weight: 700;
  color: #2e7d32;
  font-size: 14px;
}

.step3-all-hint {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
  font-size: 15px;
  color: #263238;
  padding: 8px 6px;
  line-height: 1.6;
}

.step3-project-block {
  border: 1px solid rgba(25, 118, 210, 0.18);
  border-radius: 10px;
  overflow: hidden;
}

.step3-project-header {
  display: flex;
  align-items: center;
  background: linear-gradient(90deg, #f0f6ff 0%, #f8fbff 100%);
  padding: 8px 12px;
  font-size: 13px;
  border-bottom: 1px solid rgba(25, 118, 210, 0.12);
}

.step3-project-title {
  font-weight: 600;
  color: #1565c0;
}

.step3-sum {
  font-weight: 700;
  color: #2e7d32;
  font-size: 13px;
}

.step3-detail-table {
  width: 100%;
}

.step3-detail-table thead th {
  font-size: 11px;
  color: #546e7a;
  font-weight: 600;
  background: #f9fbfd;
}

.step3-detail-table tbody td {
  font-size: 12px;
  vertical-align: middle;
}

.step2-totals-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
  background: white;
  border: 1px solid rgba(25, 118, 210, 0.2);
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 13px;
  color: #37474f;
}

.amount-total-sub {
  font-size: 11px;
  color: #78909c;
  margin-top: 2px;
}
</style>
